import styled from '@emotion/styled';
import { ThemeContext } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';

import { AppTheme } from 'theme';

import {
  GameDifficulty,
  GameTypeEnum,
  OnlineOpponent,
} from '../../../../../../graphql/types';
import {
  DEFAULT_RESULT_SIZE,
  DEFAULT_RESULT_SIZE_INCREMENT,
  POLL_INTERVAL,
  useOnlineOpponents,
} from '../../../../hooks/useOnlineOpponents';
import OnlineUsersList from './OnlineUsersList';
import OnlineUsersText from './OnlineUsersText';
import SearchTextField from '../../../SearchTextField';
import { useEffect, useState } from 'react';

interface Props {
  selectedSceneId?: string;
  gameType?: GameTypeEnum;
  onOpponentSelected: (opponent: OnlineOpponent) => void;
  gameDifficulty: GameDifficulty;
}

export const MIN_SEARCH_TERM_LENGTH = 3;

const diacrRE = /[\u0300-\u036f]/g;
const punctRE =
  /[\u2000-\u206F\u2E00-\u2E7F\\'!"#$%&()*+,\-./:;<=>?@[\]^_`{|}~]/g;
const spaceRE = /\s+/g;

const isSearchTermValid = (term?: string | null): boolean => {
  return !!(term && term.trim().length >= MIN_SEARCH_TERM_LENGTH);
};

const normalizeInput = (input: string): string => {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(diacrRE, '')
    .replace(punctRE, '')
    .replace(spaceRE, ' ')
    .trim();
};

const STOP_POLLING_ERRORS = [
  'INTERNAL_SERVER_ERROR',
  'GRAPHQL_PARSE_FAILED',
  'GRAPHQL_VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
];

const InviteUser = ({
  selectedSceneId,
  gameType,
  onOpponentSelected,
  gameDifficulty,
}: Props) => {
  const [t] = useTranslation();

  const theme = useContext(ThemeContext) as AppTheme;
  const IS_EDU = theme?.layout?.edu;

  const [searchedTerm, setSearchedTerm] = useState('');
  const [rawSearchedTerm, setRawSearchedTerm] = useState('');
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | undefined>(
    undefined,
  );
  const [resultSize, setResultSize] = useState(DEFAULT_RESULT_SIZE);
  const handleSearchTermChange = (input: string) => {
    setRawSearchedTerm(input);
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(undefined);
    }
    setTimeoutId(
      setTimeout(() => {
        setSearchedTerm(normalizeInput(input));
      }, 300),
    );
  };

  const onBottomReached = (currentSize: number) => {
    if (resultSize === currentSize) {
      setResultSize(resultSize + DEFAULT_RESULT_SIZE_INCREMENT);
    }
  };

  const {
    error,
    opponents,
    refetch,
    startPolling,
    stopPolling,
    opponentCount,
  } = useOnlineOpponents(
    selectedSceneId,
    gameType,
    resultSize,
    isSearchTermValid(searchedTerm) ? searchedTerm : null,
  );

  useEffect(() => {
    stopPolling();
    refetch();
    startPolling(POLL_INTERVAL);

    return () => {
      stopPolling();
    };
  }, [searchedTerm, refetch, startPolling, stopPolling, opponents]);

  useEffect(() => {
    if (error) {
      if (
        error.graphQLErrors &&
        error.graphQLErrors.some((gqlError) => {
          return (
            gqlError.extensions &&
            STOP_POLLING_ERRORS.includes(gqlError.extensions.code)
          );
        })
      ) {
        stopPolling();
      }
    }
  }, [error, stopPolling]);

  useEffect(() => {
    refetch();
  }, [resultSize, refetch]);

  if (error != null) {
    return null;
  }

  return (
    <>
      <OnlineUsersText
        onlineUsersCount={opponentCount}
        searchedTerm={searchedTerm}
      />

      <InvitesContainer>
        <Container css={IS_EDU ? { height: 'initial' } : undefined}>
          <div>
            {rawSearchedTerm || (opponentCount ?? 0) > 0 ? (
              <SearchTextField
                searchedTerm={rawSearchedTerm}
                onSearchedTermChange={handleSearchTermChange}
                placeholder={t(
                  'lobby.onlineUsersSearch.placeHolder',
                  'Hledat kolegu (min. 3 znaky)',
                )}
              />
            ) : null}
            <OnlineUsersList
              onlineUsers={opponents}
              onSelected={onOpponentSelected}
              gameDifficulty={gameDifficulty}
              onBottomReached={onBottomReached}
              searchActive={isSearchTermValid(searchedTerm)}
            />
          </div>
        </Container>
      </InvitesContainer>
    </>
  );
};

export default InviteUser;

const InvitesContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const HEIGHT = '160px';

const Container = styled.div`
  display: flex;
  flex-direction: row-reverse;
  /* This max-width is there to prevent stretching parent width if user's name is too long, we will overlow instead */
  max-width: 52em;
  height: ${HEIGHT};
  margin: 8px auto 8px auto;
  padding: 0;
  justify-content: center;
`;
